"use client"

import React, { useEffect, useRef } from 'react'
import {
  ChartBarIcon,
  HandRaisedIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline'
import WhyLalList from '@/data/whyLalList.json'
import Button from '@/components/Button'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

const PILLAR_META = [
  { label: 'PILLAR 1', microProof: 'Learn your options' },
  { label: 'PILLAR 2', microProof: 'Peace of mind' },
  { label: 'PILLAR 3', microProof: 'Drive your plan' },
]

const QUOTE_ITEMS = [
  {
    name: 'Warren Buffett',
    quote:
      'Clarity of purpose, confidence in principle, and control over emotion — the keys to sound investing.',
  },
  {
    name: 'Benjamin Franklin',
    quote:
      'When people understand their finances, they decide from confidence, not fear. That control builds every lasting legacy.',
  },
  {
    name: 'Albert Einstein',
    quote:
      'Discipline turns clarity into confident decisions and keeps you in control of your growth.',
  },
]

const PILLAR_ICONS = [ChartBarIcon, HandRaisedIcon, Cog6ToothIcon]

export default function WhyLal() {
  const { whyLalList } = WhyLalList

  const containerRef = useRef(null)
  const badgeRef = useRef(null)
  const headlineRef = useRef(null)
  const descRef = useRef(null)
  const proofPillRef = useRef(null)
  const cardsWrapRef = useRef(null)
  const socialStripRef = useRef(null)
  const ctaRef = useRef(null)
  const growthLineRef = useRef(null)
  const arrowHeadRef = useRef(null)
  const barsContainerRef = useRef(null)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    gsap.registerPlugin(ScrollTrigger)

    if (prefersReducedMotion) {
      gsap.set(
        [
          badgeRef.current,
          headlineRef.current,
          descRef.current,
          proofPillRef.current,
          socialStripRef.current,
          ctaRef.current,
        ],
        { autoAlpha: 1, y: 0 }
      )

      if (cardsWrapRef.current) {
        const cards = cardsWrapRef.current.querySelectorAll('.why-lal-card')
        gsap.set(cards, { autoAlpha: 1, y: 0 })
      }
      
      // Show growth line without animation
      if (growthLineRef.current) {
        gsap.set(growthLineRef.current, { strokeDashoffset: 0 })
      }
      if (arrowHeadRef.current) {
        gsap.set(arrowHeadRef.current, { opacity: 1 })
      }
      
      // Show bars without animation
      const reducedMotionBars = barsContainerRef.current?.querySelectorAll('rect') || []
      if (reducedMotionBars.length) {
        gsap.set(reducedMotionBars, { scaleY: 1 })
      }
      return
    }

    const ctx = gsap.context(() => {
      const cards = cardsWrapRef.current?.querySelectorAll('.why-lal-card') || []

      gsap.set(
        [
          badgeRef.current,
          headlineRef.current,
          descRef.current,
          proofPillRef.current,
        ],
        { autoAlpha: 0, y: 14 }
      )
      gsap.set(cards, { autoAlpha: 0, y: 16 })
      gsap.set([socialStripRef.current, ctaRef.current], { autoAlpha: 0, y: 14 })

      // Set up growth line animation
      if (growthLineRef.current) {
        const lineLength = growthLineRef.current.getTotalLength()
        gsap.set(growthLineRef.current, {
          strokeDasharray: lineLength,
          strokeDashoffset: lineLength,
        })
      }
      if (arrowHeadRef.current) {
        gsap.set(arrowHeadRef.current, { opacity: 0 })
      }

      // Set up bars animation
      const chartBars = barsContainerRef.current?.querySelectorAll('rect') || []
      if (chartBars.length) {
        gsap.set(chartBars, { scaleY: 0, transformOrigin: 'bottom' })
      }

      gsap
        .timeline({
          defaults: { ease: 'power3.out' },
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 70%',
            toggleActions: 'play none none none',
            once: true,
          },
        })
        .to([badgeRef.current, proofPillRef.current], {
          autoAlpha: 1,
          y: 0,
          duration: 0.75,
          stagger: 0.1,
        })
        .to(
          headlineRef.current,
          { autoAlpha: 1, y: 0, duration: 0.85 },
          '-=0.35'
        )
        .to(
          descRef.current,
          { autoAlpha: 1, y: 0, duration: 0.75 },
          '-=0.55'
        )
        .to(
          cards,
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.9,
            stagger: 0.12,
          },
          '-=0.45'
        )
        .to(
          socialStripRef.current,
          { autoAlpha: 1, y: 0, duration: 0.8 },
          '-=0.55'
        )
        .to(
          ctaRef.current,
          { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power3.out' },
          '-=0.6'
        )

      // Growth line drawing animation
      if (growthLineRef.current) {
        gsap.to(growthLineRef.current, {
          strokeDashoffset: 0,
          duration: 2,
          ease: 'power2.inOut',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 70%',
            toggleActions: 'play none none none',
            once: true,
          },
        })
      }
      if (arrowHeadRef.current) {
        gsap.to(arrowHeadRef.current, {
          opacity: 1,
          duration: 0.4,
          delay: 1.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 70%',
            toggleActions: 'play none none none',
            once: true,
          },
        })
      }

      // Bars animation - grow from bottom
      const animatedBars = barsContainerRef.current?.querySelectorAll('rect') || []
      if (animatedBars.length) {
        gsap.to(animatedBars, {
          scaleY: 1,
          duration: 1.5,
          ease: 'power4.out',
          stagger: {
            amount: 0.6,
            from: 'start',
          },
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 70%',
            toggleActions: 'play none none none',
            once: true,
          },
        })
      }
    }, containerRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      id="about"
      ref={containerRef}
      className="relative w-full overflow-hidden bg-primary py-16 sm:py-20 lg-first:py-24"
      aria-labelledby="why-lal-heading"
    >
      {/* --- BACKGROUND LAYERS (match StartToServices, slightly lighter) --- */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-gray-900/60 via-gray-950 to-black z-0 pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_var(--tw-gradient-stops))] from-secondary/5 via-transparent to-transparent z-0 pointer-events-none" />
      <div className="absolute inset-0 opacity-[0.02] bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] z-0 pointer-events-none mix-blend-overlay" />

      {/* Growth chart - bar chart with ascending arrow - full width */}
      <svg
        className="absolute bottom-0 left-[2rem] w-full h-[45%] max-xs:h-[40%] sm:h-[55%] md:h-[65%] lg-first:h-[70%] z-[1] pointer-events-none opacity-15 max-xs:opacity-12 sm:opacity-20 md:opacity-25 lg-first:opacity-30"
        viewBox="0 0 1050 420"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {/* Definitions for gradient and glow */}
        <defs>
          <linearGradient id="barGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#9A7B2A" stopOpacity="1" />
            <stop offset="20%" stopColor="#6B5620" stopOpacity="0.95" />
            <stop offset="50%" stopColor="#3D3010" stopOpacity="0.8" />
            <stop offset="75%" stopColor="#1a1a1a" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0.4" />
          </linearGradient>
          <linearGradient id="lineGradient" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#1a1a1a" stopOpacity="0.5" />
            <stop offset="40%" stopColor="#8B7320" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#DFAF37" stopOpacity="1" />
          </linearGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* Growth line - thinner stroke version with gradient */}
        <path
          ref={growthLineRef}
          fill="none"
          stroke="url(#lineGradient)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M 35 310 L 240 210 L 365 270 L 493 165 L 620 200 L 977 5"
          filter="url(#glow)"
        />
        {/* Arrow head - clean chevron style rotated, brighter for visibility */}
        <g ref={arrowHeadRef}>
          <path
            fill="none"
            stroke="#DFAF37"
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M 948,11 L 960,-2 L 972,11"
            transform="rotate(49, 963, 20)"
            filter="url(#glow)"
          />
        </g>
        {/* Bars - 15 bars with gradient and glow */}
        <g ref={barsContainerRef}>
          <rect x="20" y="330" width="45" height="70" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
          <rect x="85" y="300" width="45" height="100" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
          <rect x="150" y="260" width="45" height="140" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
          <rect x="215" y="230" width="45" height="170" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
          <rect x="280" y="270" width="45" height="130" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
          <rect x="345" y="290" width="45" height="110" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
          <rect x="410" y="250" width="45" height="150" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
          <rect x="475" y="200" width="45" height="200" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
          <rect x="540" y="230" width="45" height="170" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
          <rect x="605" y="210" width="45" height="190" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
          <rect x="670" y="180" width="45" height="220" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
          <rect x="735" y="150" width="45" height="250" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
          <rect x="800" y="120" width="45" height="280" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
          <rect x="865" y="80" width="45" height="320" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
          <rect x="930" y="50" width="45" height="350" fill="url(#barGradient)" rx="4" ry="4" filter="url(#glow)" />
        </g>
      </svg>

      <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg-first:px-8">
        {/* ----- HEADER ROW ----- */}
        <div className="mb-12 grid grid-cols-1 gap-6 lg-first:mb-16 lg-first:grid-cols-[1fr_auto] lg-first:items-start">
          <div>
            <span
              ref={badgeRef}
              className="mb-3 inline-block rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gray-300"
            >
              Why LAL
            </span>
            <h2
              id="why-lal-heading"
              ref={headlineRef}
              className="font-montserrat text-4xl font-bold tracking-tight text-white sm:text-5xl lg-first:text-6xl"
            >
              THE LAL METHOD
            </h2>
            <p
              ref={descRef}
              className="mt-4 max-w-2xl text-base leading-relaxed text-gray-300 sm:text-lg"
            >
              From confusion to clarity, fear to confidence, and helplessness to
              control — a proven method to transform your relationship with
              money.
            </p>
          </div>
          <div className="hidden shrink-0 lg-first:block">
            <span
              ref={proofPillRef}
              className="inline-block rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-xs text-gray-400"
            >
              Trusted partner • Education-first • Strategy + protection
            </span>
          </div>
        </div>

        {/* ----- THREE PILLAR CARDS ----- */}
        <div
          ref={cardsWrapRef}
          className="mb-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg-first:grid-cols-3"
        >
          {whyLalList.map((item, index) => {
            const Icon = PILLAR_ICONS[index]
            const meta = PILLAR_META[index]
            return (
              <article
                key={item.id}
                className="why-lal-card group relative rounded-2xl border border-white/10 bg-white/[0.06] p-6 shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-0.5 hover:border-white/15 hover:shadow-xl hover:shadow-black/30 sm:p-8"
              >
                <span className="mb-4 block text-xs font-semibold uppercase tracking-wider text-secondary/90">
                  {meta.label}
                </span>
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-white/5 text-secondary">
                  <Icon className="h-6 w-6" strokeWidth={1.8} />
                </div>
                <h3 className="font-montserrat text-xl font-bold text-white sm:text-2xl">
                  {item.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-300 sm:text-base">
                  {item.description}
                </p>
                <p className="mt-4 flex items-center gap-2 text-xs text-secondary/90">
                  <span className="h-1 w-1 rounded-full bg-secondary/80" />
                  {meta.microProof}
                </p>
              </article>
            )
          })}
        </div>

        {/* ----- SOCIAL PROOF STRIP ----- */}
        <div
          ref={socialStripRef}
          className="mb-14 rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8 lg-first:p-10"
        >
          <h3 className="text-lg font-semibold text-white sm:text-xl">
            Social proof that mirrors the method
          </h3>
          <p className="mt-1 text-sm text-gray-400">
            Short quotes reinforce clarity, confidence, and control — so the
            message sticks.
          </p>
          <div className="mt-8 flex flex-col gap-6 md:flex-row md:overflow-x-auto md:gap-4 md:pb-2 [scrollbar-width:thin]">
            {QUOTE_ITEMS.map((q) => (
              <blockquote
                key={q.name}
                className="min-w-[280px] shrink-0 rounded-xl border border-white/10 bg-white/[0.06] p-5 shadow-md transition-shadow hover:shadow-lg sm:min-w-0 sm:flex-1"
              >
                <span className="font-serif text-4xl leading-none text-secondary/70">
                  &ldquo;
                </span>
                <p className="mt-2 text-sm leading-relaxed text-gray-300 sm:text-base">
                  {q.quote}
                </p>
                <footer className="mt-3 text-xs font-semibold text-secondary">
                  — {q.name}
                </footer>
              </blockquote>
            ))}
          </div>
        </div>

        {/* ----- CTA ----- */}
        <div ref={ctaRef} className="flex justify-end">
          <a href="#contact" className="inline-block">
            <Button
              variant="primary"
              className="rounded-full px-8 py-3 text-sm font-bold uppercase tracking-wider text-black shadow-lg shadow-secondary/30 transition-all duration-300 hover:scale-105 hover:shadow-secondary/40"
            >
              Explore The LAL Method
            </Button>
          </a>
        </div>
      </div>
    </section>
  )
}
